import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToCommissions, createCommission } from '../../services/commissionsService';

const TIPOS = [
  { value: 'MANTENIMIENTO_AEROTERMIA',          label: 'Mantenimiento aerotermia',     amount: 5 },
  { value: 'EXTENSION_GARANTIA',                label: 'Extensión de garantía',        amount: 5 },
  { value: 'FILTRO_POLIFOSFATOS',               label: 'Filtro de polifosfatos',       amount: 2.5 },
  { value: 'MAS_INCIDENCIAS',                   label: 'Más incidencias',              amount: 25 },
  { value: 'VENTA_EQUIPO_NUEVO',                label: 'Venta de equipo nuevo',        amount: 25 },
  { value: 'MEJOR_VALORADO',                    label: 'Mejor valorado (encuesta)',    amount: 20 },
  { value: 'CAPTACION_CONTRATO_MANTENIMIENTO',  label: 'Captación contrato mant.',     amount: 10 },
];

const TIPO_LABEL = Object.fromEntries(TIPOS.map(t => [t.value, t.label]));

const EMPTY_FORM = { workorderId: '', tipo: 'MANTENIMIENTO_AEROTERMIA', observaciones: '' };

function CommissionCard({ c }) {
  const isPaid = c.estado === 'PAGADA';
  return (
    <div className="bg-white rounded-xl p-3.5 border flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">
          {TIPO_LABEL[c.tipo] || c.tipo}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {c.workorderId ? `Orden #${c.workorderId} · ` : ''}
          {c.fechaCreacion
            ? new Date(c.fechaCreacion).toLocaleDateString('es-ES')
            : ''}
        </p>
        {c.observaciones && (
          <p className="text-xs text-gray-400 mt-0.5 italic truncate">{c.observaciones}</p>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-base font-bold text-gray-900">
          {typeof c.importe === 'number' ? c.importe.toFixed(2) : c.importe}€
        </p>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>
          {isPaid ? 'Pagada' : 'Pendiente'}
        </span>
      </div>
    </div>
  );
}

export default function TechnicianCommissionsView() {
  const { user } = useAuth();
  const [allCommissions, setAllCommissions] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [showForm, setShowForm]             = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);

  useEffect(() => {
    const unsub = subscribeToCommissions(
      (data) => { setAllCommissions(data); setLoading(false); },
      ()     => { setError('Error al cargar comisiones'); setLoading(false); }
    );
    return unsub;
  }, []);

  const commissions = allCommissions.filter(
    c => String(c.employeeId) === String(user?.employeeId)
  );

  const pending      = commissions.filter(c => c.estado === 'PENDIENTE');
  const paid         = commissions.filter(c => c.estado === 'PAGADA');
  const totalPending = pending.reduce((s, c) => s + (Number(c.importe) || 0), 0);

  const selectedTipo = TIPOS.find(t => t.value === form.tipo);

  const handleAdd = async () => {
    if (!form.tipo) return;
    setSaving(true);
    setError('');
    try {
      await createCommission({
        employeeId:    user.employeeId,
        employeeName:  user.name,
        workorderId:   form.workorderId || '',
        tipo:          form.tipo,
        importe:       selectedTipo?.amount ?? 0,
        observaciones: form.observaciones,
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(e.message || 'Error al guardar la comisión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">

      {/* Resumen */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Comisiones</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {pending.length} pendiente{pending.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-green-600">{totalPending.toFixed(2)} €</p>
          <p className="text-xs text-gray-400">Por cobrar</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <span>⚠️</span>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <button
        onClick={() => setShowForm(true)}
        className="w-full py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold rounded-2xl transition-colors text-sm"
      >
        + Añadir comisión
      </button>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
        </div>
      ) : commissions.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <div className="text-5xl mb-3">💶</div>
          <p className="font-semibold">Sin comisiones registradas</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pending.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Pendientes de cobro</p>
              {pending.map(c => <CommissionCard key={c.id} c={c} />)}
            </div>
          )}
          {paid.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Pagadas</p>
              {paid.map(c => <CommissionCard key={c.id} c={c} />)}
            </div>
          )}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/50">
          <div className="bg-white w-full rounded-t-3xl p-6 space-y-4">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
            <h3 className="font-bold text-gray-900 text-lg">Nueva comisión</h3>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tipo</label>
              <select
                value={form.tipo}
                onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white"
              >
                {TIPOS.map(t => (
                  <option key={t.value} value={t.value}>{t.label} — {t.amount}€</option>
                ))}
              </select>
              {selectedTipo && (
                <p className="mt-1.5 text-xs text-green-600 font-semibold">Importe: {selectedTipo.amount}€</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Nº Orden <span className="font-normal text-gray-400">(opcional)</span>
              </label>
              <input
                type="text"
                value={form.workorderId}
                onChange={e => setForm(f => ({ ...f, workorderId: e.target.value }))}
                placeholder="Ej: 12345"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Notas <span className="font-normal text-gray-400">(opcional)</span>
              </label>
              <textarea
                value={form.observaciones}
                onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
                rows={2}
                placeholder="Información adicional..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 font-semibold rounded-xl transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleAdd}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors text-sm disabled:opacity-50"
              >
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Añadir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
